import { importKlinesToDb, importIndicatorCsvToDb, getKlinesDateRange } from "./importKlines";

// Standard Binance format files (microsecond timestamps)
const BINANCE_CSV_FILES = [
  "/data/BTCUSDT-1m-2025-01.csv",
  "/data/BTCUSDT-1m-2025-02.csv",
  "/data/BTCUSDT-1m-2025-03.csv",
  "/data/BTCUSDT-1m-2025-04.csv",
  "/data/BTCUSDT-1m-2025-05.csv",
  "/data/BTCUSDT-1m-2025-06.csv",
  "/data/BTCUSDT-1m-2025-07.csv",
  "/data/BTCUSDT-1m-2025-08.csv",
  "/data/BTCUSDT-1m-2025-09.csv",
  "/data/BTCUSDT-1m-2025-10.csv",
  "/data/BTCUSDT-1m-2025-11.csv",
  "/data/BTCUSDT-1m-2025-12.csv",
];

// Indicator format files (second timestamps with headers)
const INDICATOR_CSV_FILES = [
  "/data/indicator/BTCUSDT-1m-ind-01.csv",
  "/data/indicator/BTCUSDT-1m-ind-02.csv",
  "/data/indicator/BTCUSDT-1m-ind-03.csv",
  "/data/indicator/BTCUSDT-1m-ind-04.csv",
  "/data/indicator/BTCUSDT-1m-ind-05.csv",
  "/data/indicator/BTCUSDT-1m-ind-06.csv",
  "/data/indicator/BTCUSDT-1m-ind-07.csv",
  "/data/indicator/BTCUSDT-1m-ind-08.csv",
  "/data/indicator/BTCUSDT-1m-ind-09.csv",
  "/data/indicator/BTCUSDT-1m-ind-10.csv",
  "/data/indicator/BTCUSDT-1m-ind-11.csv",
];

export async function runFullImport() {
  console.log("Starting BTCUSDT 1m klines import...");
  console.log(`Binance format files: ${BINANCE_CSV_FILES.length}`);
  console.log(`Indicator format files: ${INDICATOR_CSV_FILES.length}`);

  let totalInserted = 0;
  const allErrors: string[] = [];

  // Import Binance format files
  console.log("\n=== Importing Binance format files ===");
  for (let i = 0; i < BINANCE_CSV_FILES.length; i++) {
    const file = BINANCE_CSV_FILES[i];
    console.log(`\n[${i + 1}/${BINANCE_CSV_FILES.length}] Fetching ${file}...`);

    try {
      const response = await fetch(file);
      if (!response.ok) {
        console.error(`Failed to fetch ${file}: ${response.status}`);
        allErrors.push(`Failed to fetch ${file}`);
        continue;
      }

      const csvData = await response.text();
      console.log(`  Loaded ${csvData.split("\n").length} lines`);

      const result = await importKlinesToDb(csvData, "BTCUSDT", "1m", (progress) => {
        if (progress.current % 5000 === 0 || progress.current === progress.total) {
          console.log(`  Progress: ${progress.current}/${progress.total} (${Math.round(progress.current / progress.total * 100)}%)`);
        }
      });

      totalInserted += result.inserted;
      if (result.errors.length > 0) {
        allErrors.push(...result.errors);
      }

      console.log(`  ✓ Inserted ${result.inserted} rows`);
    } catch (error) {
      console.error(`Error processing ${file}:`, error);
      allErrors.push(`Error processing ${file}: ${error}`);
    }
  }

  // Import indicator format files
  console.log("\n=== Importing Indicator format files ===");
  for (let i = 0; i < INDICATOR_CSV_FILES.length; i++) {
    const file = INDICATOR_CSV_FILES[i];
    console.log(`\n[${i + 1}/${INDICATOR_CSV_FILES.length}] Fetching ${file}...`);

    try {
      const response = await fetch(file);
      if (!response.ok) {
        console.error(`Failed to fetch ${file}: ${response.status}`);
        allErrors.push(`Failed to fetch ${file}`);
        continue;
      }

      const csvData = await response.text();
      console.log(`  Loaded ${csvData.split("\n").length} lines`);

      const result = await importIndicatorCsvToDb(csvData, "BTCUSDT", "1m", (progress) => {
        if (progress.current % 5000 === 0 || progress.current === progress.total) {
          console.log(`  Progress: ${progress.current}/${progress.total} (${Math.round(progress.current / progress.total * 100)}%)`);
        }
      });

      totalInserted += result.inserted;
      if (result.errors.length > 0) {
        allErrors.push(...result.errors);
      }

      console.log(`  ✓ Inserted ${result.inserted} rows`);
    } catch (error) {
      console.error(`Error processing ${file}:`, error);
      allErrors.push(`Error processing ${file}: ${error}`);
    }
  }

  console.log("\n========================================");
  console.log("Import complete!");
  console.log(`Total rows inserted: ${totalInserted}`);

  if (allErrors.length > 0) {
    console.log(`Errors: ${allErrors.length}`);
    allErrors.forEach(e => console.error(`  - ${e}`));
  }

  // Check final date range
  const range = await getKlinesDateRange("BTCUSDT", "1m");
  console.log(`\nDatabase now contains:`);
  console.log(`  From: ${range.from}`);
  console.log(`  To: ${range.to}`);
  console.log(`  Total rows: ${range.count}`);

  return { totalInserted, errors: allErrors, range };
}

// Make it available on window (and parent/top when possible) for console access

declare global {
  interface Window {
    runKlinesImport?: typeof runFullImport;
    runFullImport?: typeof runFullImport;
  }
}

if (typeof window !== "undefined") {
  // Current frame
  window.runKlinesImport = runFullImport;
  window.runFullImport = runFullImport;

  // Also attach to globalThis for consistency
  (globalThis as any).runKlinesImport = runFullImport;
  (globalThis as any).runFullImport = runFullImport;

  // In some DevTools contexts, setting window.* doesn't create an unqualified global identifier.
  // Use an indirect eval to define globals in the current frame.
  try {
    (0, eval)(
      "var runKlinesImport = globalThis.runKlinesImport; var runFullImport = globalThis.runFullImport;"
    );
  } catch {
    // ignore
  }

  // In some preview environments, DevTools can sometimes be focused on the parent/top frame.
  // Expose the function there too (safe no-op if cross-origin).
  try {
    (window.parent as any).runKlinesImport = runFullImport;
    (window.parent as any).runFullImport = runFullImport;
    (window.parent as any).eval?.(
      "var runKlinesImport = window.runKlinesImport; var runFullImport = window.runFullImport;"
    );
  } catch {
    // ignore
  }

  try {
    (window.top as any).runKlinesImport = runFullImport;
    (window.top as any).runFullImport = runFullImport;
    (window.top as any).eval?.(
      "var runKlinesImport = window.runKlinesImport; var runFullImport = window.runFullImport;"
    );
  } catch {
    // ignore
  }
}


